import React from "react";
import { Wrap, SectionHead, Note } from "./Section";
import { useReveal } from "../hooks/useReveal";

function AccountCard({ mult, deposit, r05, r1, ddl, losses, accent }) {
  const [ref, inView] = useReveal();
  return (
    <div ref={ref} className={`reveal ${inView ? "in" : ""} card rounded-2xl p-8 ${accent ? "border-accent/30" : ""}`}>
      <div className="flex items-center justify-between mb-6">
        <span className="label text-[var(--muted)]">{mult} account</span>
        <span className="display text-accent text-4xl">{mult}</span>
      </div>
      <div className="mono text-lg text-[var(--text)] mb-6">{deposit}</div>

      <div className="space-y-3 mb-6">
        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
          <span className="text-[13px] text-[var(--muted)]">{r05.left}</span>
          <span className="text-[13px] text-accent font-medium">{r05.right}</span>
        </div>
        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
          <span className="text-[13px] text-[var(--muted)]">{r1.left}</span>
          <span className="text-[13px] text-accent font-medium">{r1.right}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="label text-[var(--muted)] text-[0.58rem] mb-2">Account drawdown limit</div>
          <div className="display text-2xl">{ddl}</div>
          <div className="text-[11px] text-[var(--muted)] mt-1">Reached, the account closes automatically.</div>
        </div>
        <div>
          <div className="label text-[var(--muted)] text-[0.58rem] mb-2">Consecutive losses to closure</div>
          <div className="display text-2xl">{losses}</div>
          <div className="text-[11px] text-[var(--muted)] mt-1">At the strategy's historical risk per trade.</div>
        </div>
      </div>
    </div>
  );
}

export default function Accounts() {
  return (
    <div>
      <Wrap id="accounts">
        <SectionHead
          tag="Account structures"
          title="12× and 24×: same percentage,"
          highlight="different arithmetic"
          desc="The strategy targets the same 0.5–1% per month on trading volume in both structures. The multiplier decides what that becomes on your deposit — and how much room the account has to move against you before it closes."
        />
        <div className="mt-12 grid md:grid-cols-2 gap-5">
          <AccountCard mult="12×" deposit="$1,000 → $12,000"
            r05={{ left: "0.5% on $12,000", right: "= 6% on your investment" }}
            r1={{ left: "1% on $12,000", right: "= 12% on your investment" }}
            ddl="10%" losses="200" />
          <AccountCard mult="24×" deposit="$1,000 → $24,000" accent
            r05={{ left: "0.5% on $24,000", right: "= 12% on your investment" }}
            r1={{ left: "1% on $24,000", right: "= 24% on your investment" }}
            ddl="5%" losses="100" />
        </div>
        <Note>
          The trade-off in one line: 24× doubles the effect of the same monthly percentage on your deposit, and halves the drawdown the account can absorb before it is closed — 5% instead of 10%. More amplification is not strictly better; it is a different risk position.
        </Note>
      </Wrap>
    </div>
  );
}
